import React, { useCallback, useEffect, useRef, useState } from "react";
import { loadGoogleMaps } from "../propertydetails/NearbyMap";
import { sessionStorageService } from "../../features/authentication";
import { authedFetch } from "./Helpers";
import { ENDPOINTS, MAX_AREA_SHAPES } from "./Constants";

/**
 * Stage 2 (location) of the inline "Talk to Me" onboarding: the areas the
 * backend resolved (AiChatResponse.onboardingAreaPins -- each with its real
 * boundary, a road's path, or an `approximate` fallback octagon) on one map,
 * which the user can reshape, redraw, remove, or add to (up to
 * MAX_AREA_SHAPES) before confirming. onConfirm(names, shapes) sends the
 * final AreaShape list (GeoJSON, [lng, lat]); the backend searches with
 * these exact shapes instead of re-geocoding the names.
 *
 * Polygons are reshaped by dragging their vertices; anything else (a road
 * line, a multi-part area, a new area) is drawn by tapping points on the
 * map -- the Maps drawing library this used to need was retired.
 */

const toLatLngs = (ring) => {
    const pts = ring.map(([lng, lat]) => ({ lat, lng }));
    const first = pts[0], last = pts[pts.length - 1];
    return pts.length > 1 && first.lat === last.lat && first.lng === last.lng ? pts.slice(0, -1) : pts;
};

const polygonFrom = (points) => {
    const ring = points.map(({ lat, lng }) => [lng, lat]);
    return { type: "Polygon", coordinates: [[...ring, ring[0]]] };
};

const centroidOf = (points) => ({
    lat: points.reduce((s, p) => s + p.lat, 0) / points.length,
    lng: points.reduce((s, p) => s + p.lng, 0) / points.length,
});

const isReshapable = (shape) => shape.geometry?.type === "Polygon" && shape.geometry.coordinates.length === 1;

const toShape = (pin) => ({
    name: pin.name,
    lat: pin.lat,
    lng: pin.lng,
    placeId: pin.placeId ?? null,
    kind: pin.kind ?? null,
    geometry: pin.geometry ?? null,
    edited: false,
    approximate: !!pin.approximate,
});

export default function AreaShapeEditor({ pins, disabled, onConfirm }) {
    const [shapes, setShapes] = useState(() => pins.map(toShape));
    const [editingIndex, setEditingIndex] = useState(null);
    // { index } redraws an existing shape; { name } draws a new one.
    const [drawTarget, setDrawTarget] = useState(null);
    const [drawPoints, setDrawPoints] = useState([]);
    const [adding, setAdding] = useState(false);
    const [newName, setNewName] = useState("");
    const [lookupBusy, setLookupBusy] = useState(false);
    const [lookupError, setLookupError] = useState(null);
    const [confirmedLabel, setConfirmedLabel] = useState(null);
    const [mapReady, setMapReady] = useState(false);

    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const overlaysRef = useRef([]);
    const fittedCountRef = useRef(-1);
    const drawTargetRef = useRef(drawTarget);
    drawTargetRef.current = drawTarget;

    const locked = disabled || !!confirmedLabel;
    const busyEditing = editingIndex !== null || drawTarget !== null;

    useEffect(() => {
        let cancelled = false;
        loadGoogleMaps()
            .then(() => {
                if (cancelled || !containerRef.current) return;
                const G = window.google.maps;
                const map = new G.Map(containerRef.current, {
                    zoom: 13,
                    center: { lat: pins[0]?.lat ?? 12.9716, lng: pins[0]?.lng ?? 77.5946 },
                    disableDefaultUI: true,
                    zoomControl: true,
                    clickableIcons: false,
                });
                map.addListener("click", (e) => {
                    if (!drawTargetRef.current) return;
                    setDrawPoints((prev) => [...prev, { lat: e.latLng.lat(), lng: e.latLng.lng() }]);
                });
                mapRef.current = map;
                setMapReady(true);
            })
            .catch(() => {
                // Map failed to load -- the area chips and Confirm still work without it.
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateShape = useCallback((index, patch) => {
        setShapes((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
    }, []);

    // Redraw every overlay whenever the shapes or the edit mode change.
    useEffect(() => {
        const map = mapRef.current;
        if (!mapReady || !map) return;
        const G = window.google.maps;
        overlaysRef.current.forEach((o) => { G.event.clearInstanceListeners(o); o.setMap(null); });
        overlaysRef.current = [];
        const bounds = new G.LatLngBounds();
        const add = (overlay) => { overlaysRef.current.push(overlay); return overlay; };

        shapes.forEach((shape, i) => {
            const active = i === editingIndex || drawTarget?.index === i;
            const style = {
                strokeColor: active ? "#f97316" : "#1e3a8a",
                strokeWeight: active ? 3 : 2,
                fillColor: active ? "#f97316" : "#1e3a8a",
                fillOpacity: shape.approximate ? 0.06 : 0.12,
                map,
            };
            const geom = shape.geometry;
            if (geom && drawTarget?.index !== i) {
                if (geom.type === "LineString") {
                    const path = geom.coordinates.map(([lng, lat]) => ({ lat, lng }));
                    add(new G.Polyline({ ...style, path, strokeOpacity: 0.9 }));
                    path.forEach((p) => bounds.extend(p));
                } else {
                    const parts = geom.type === "Polygon" ? [geom.coordinates] : geom.coordinates;
                    parts.forEach((rings) => {
                        const paths = rings.map(toLatLngs);
                        const editable = i === editingIndex && isReshapable(shape);
                        const polygon = add(new G.Polygon({ ...style, paths, editable }));
                        paths[0].forEach((p) => bounds.extend(p));
                        if (editable) {
                            const sync = () => {
                                const pts = polygon.getPath().getArray().map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
                                if (pts.length >= 3) {
                                    updateShape(i, { geometry: polygonFrom(pts), edited: true, approximate: false });
                                }
                            };
                            ["set_at", "insert_at", "remove_at"].forEach((ev) => polygon.getPath().addListener(ev, sync));
                        }
                    });
                }
            }
            add(new G.Marker({ position: { lat: shape.lat, lng: shape.lng }, map, title: shape.name }));
            bounds.extend({ lat: shape.lat, lng: shape.lng });
        });

        if (drawTarget && drawPoints.length) {
            const style = { strokeColor: "#f97316", strokeWeight: 3, fillColor: "#f97316", fillOpacity: 0.15, map };
            add(drawPoints.length >= 3
                ? new G.Polygon({ ...style, paths: drawPoints })
                : new G.Polyline({ ...style, path: drawPoints }));
        }

        if (fittedCountRef.current !== shapes.length && !bounds.isEmpty()) {
            map.fitBounds(bounds, 40);
            fittedCountRef.current = shapes.length;
        }
    }, [mapReady, shapes, editingIndex, drawTarget, drawPoints, updateShape]);

    const startDrawing = (target) => {
        setEditingIndex(null);
        setDrawPoints([]);
        setDrawTarget(target);
    };

    const cancelDrawing = () => {
        setDrawTarget(null);
        setDrawPoints([]);
    };

    const finishDrawing = () => {
        if (drawPoints.length < 3 || !drawTarget) return;
        const geometry = polygonFrom(drawPoints);
        if (drawTarget.index !== undefined) {
            updateShape(drawTarget.index, { geometry, edited: true, approximate: false });
        } else {
            const { lat, lng } = centroidOf(drawPoints);
            setShapes((prev) => [...prev, {
                name: drawTarget.name, lat, lng, placeId: null, kind: "drawn",
                geometry, edited: true, approximate: false,
            }]);
        }
        cancelDrawing();
    };

    const toggleEdit = (index) => {
        if (drawTarget) cancelDrawing();
        if (!isReshapable(shapes[index])) {
            startDrawing({ index });
            return;
        }
        setEditingIndex((cur) => (cur === index ? null : index));
    };

    const removeShape = (index) => {
        setEditingIndex(null);
        cancelDrawing();
        setShapes((prev) => prev.filter((_, i) => i !== index));
    };

    const closeAddPanel = () => {
        setAdding(false);
        setNewName("");
        setLookupError(null);
    };

    const lookupArea = async () => {
        const name = newName.trim();
        if (!name || lookupBusy) return;
        if (shapes.some((s) => s.name.toLowerCase() === name.toLowerCase())) {
            setLookupError(`"${name}" is already on the map.`);
            return;
        }
        setLookupBusy(true);
        setLookupError(null);
        try {
            const pin = await authedFetch(ENDPOINTS.AREA_LOOKUP, sessionStorageService.get(), {
                method: "POST",
                body: JSON.stringify({ name }),
            });
            if (pin.placeId && shapes.some((s) => s.placeId === pin.placeId)) {
                setLookupError(`"${pin.name}" is already on the map.`);
                return;
            }
            setShapes((prev) => [...prev, toShape(pin)]);
            closeAddPanel();
        } catch (err) {
            setLookupError(err.message || `Couldn't find "${name}".`);
        } finally {
            setLookupBusy(false);
        }
    };

    const drawNewArea = () => {
        const name = newName.trim() || `Drawn area ${shapes.length + 1}`;
        closeAddPanel();
        startDrawing({ name });
    };

    const handleConfirm = () => {
        if (locked || busyEditing || !shapes.length) return;
        const label = shapes.map((s) => s.name).join("; ");
        setConfirmedLabel(label);
        onConfirm?.(label, shapes);
    };

    if (confirmedLabel) {
        return <div className="msg-area-confirmed">✓ Confirmed: {confirmedLabel}</div>;
    }

    const approximate = shapes.filter((s) => s.approximate);
    const drawingName = drawTarget
        ? (drawTarget.index !== undefined ? shapes[drawTarget.index]?.name : drawTarget.name)
        : null;

    return (
        <div className="msg-area-confirm area-shape-editor">
            <div
                ref={containerRef}
                className={`msg-area-map${drawTarget ? " msg-area-map-editable" : ""}`}
                role="application"
                aria-label={`Map of ${shapes.map((s) => s.name).join(", ") || "your areas"}`}
            />

            <div className="area-shape-chips">
                {shapes.map((shape, i) => {
                    const active = editingIndex === i || drawTarget?.index === i;
                    const editLabel = isReshapable(shape)
                        ? (editingIndex === i ? "Done" : "Edit")
                        : (drawTarget?.index === i ? "Drawing…" : "Redraw");
                    return (
                        <span key={`${shape.name}-${i}`} className={`area-shape-chip${active ? " area-shape-chip-editing" : ""}`}>
                            {shape.name}
                            {shape.approximate && <span className="area-shape-chip-approx">approx.</span>}
                            <button
                                type="button"
                                className="area-shape-chip-edit"
                                disabled={locked || (busyEditing && !active)}
                                onClick={() => toggleEdit(i)}
                            >
                                {editLabel}
                            </button>
                            <button
                                type="button"
                                className="area-shape-chip-remove"
                                aria-label={`Remove ${shape.name}`}
                                disabled={locked || shapes.length <= 1 || busyEditing}
                                onClick={() => removeShape(i)}
                            >
                                ×
                            </button>
                        </span>
                    );
                })}
            </div>

            {approximate.length > 0 && !busyEditing && (
                <p className="msg-area-confirm-hint area-shape-approx-hint">
                    <strong>Approximate:</strong> we couldn't find an exact boundary for{" "}
                    {approximate.map((s) => s.name).join(", ")} — tap Edit to reshape it to the area you mean.
                </p>
            )}
            {editingIndex !== null && (
                <p className="msg-area-confirm-hint">
                    Drag the points to reshape <strong>{shapes[editingIndex]?.name}</strong>, then tap Done.
                </p>
            )}
            {drawTarget && (
                <div className="area-shape-add-panel">
                    <span className="msg-area-confirm-hint" style={{ margin: 0, flex: "1 1 160px" }}>
                        Tap the map to outline <strong>{drawingName}</strong> ({drawPoints.length} point{drawPoints.length === 1 ? "" : "s"}).
                    </span>
                    <button type="button" className="area-shape-add-submit" disabled={drawPoints.length < 3} onClick={finishDrawing}>
                        Finish
                    </button>
                    <button type="button" className="area-shape-add-draw" disabled={!drawPoints.length}
                            onClick={() => setDrawPoints((p) => p.slice(0, -1))}>
                        Undo
                    </button>
                    <button type="button" className="area-shape-add-cancel" aria-label="Cancel drawing" onClick={cancelDrawing}>
                        ×
                    </button>
                </div>
            )}

            {!busyEditing && (
                <div className="area-shape-add-row">
                    {adding ? (
                        <div className="area-shape-add-panel">
                            <input
                                className="area-shape-add-input"
                                placeholder="Locality, road or landmark"
                                value={newName}
                                autoFocus
                                disabled={lookupBusy}
                                onChange={(e) => setNewName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") lookupArea(); if (e.key === "Escape") closeAddPanel(); }}
                            />
                            <button type="button" className="area-shape-add-submit" disabled={!newName.trim() || lookupBusy}
                                    onClick={lookupArea}>
                                {lookupBusy ? "Finding…" : "Find"}
                            </button>
                            <button type="button" className="area-shape-add-draw" disabled={lookupBusy} onClick={drawNewArea}>
                                Draw on map
                            </button>
                            <button type="button" className="area-shape-add-cancel" aria-label="Cancel" onClick={closeAddPanel}>
                                ×
                            </button>
                            {lookupError && <p className="area-shape-add-error">{lookupError}</p>}
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="area-shape-add-btn"
                            disabled={locked || shapes.length >= MAX_AREA_SHAPES}
                            onClick={() => setAdding(true)}
                        >
                            + Add locality {shapes.length >= MAX_AREA_SHAPES ? `(max ${MAX_AREA_SHAPES})` : ""}
                        </button>
                    )}
                </div>
            )}

            <button
                type="button"
                className="msg-area-confirm-btn"
                disabled={locked || busyEditing || !shapes.length}
                onClick={handleConfirm}
            >
                {shapes.length > 1 ? "Confirm these areas" : "Confirm area"}
            </button>
        </div>
    );
}
